import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  AcademicCapIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { describeRoles } from '../../types/auth';
import { useClubSettings } from '../../contexts/ClubSettingsContext';
import { getNavigationGroups, NavGroup } from './navigation';
import { clubLogoSrc, handleClubLogoError } from '../../utils/clubLogo';
import { displayName } from '../../utils/brand';
import LearningModeSwitch from '../help/LearningModeSwitch';
import { useWhatsNew } from '../../hooks/useWhatsNew';
import { useAiStatus } from '../../hooks/useAiStatus';

interface MobileMenuSheetProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Alulról felúszó, teljes képernyős mobil menülap. A főnézet a menücsoportok
 * kártyarácsa; egy csoportra koppintva a tartalom oldalra csúszva mutatja az
 * almenüt (két 50%-os panel egy 200% széles sávban).
 */
const MobileMenuSheet: React.FC<MobileMenuSheetProps> = ({ open, onClose }) => {
  const { user, logout } = useAuth();
  const { settings: clubSettings } = useClubSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSub, setActiveSub] = useState<string | null>(null);
  const { hasUnseen: hasNews } = useWhatsNew();

  const aiStatus = useAiStatus();
  const groups = getNavigationGroups(user, clubSettings?.membershipFeesEnabled ?? false, aiStatus.available);
  const activeGroup: NavGroup | undefined = groups.find((g) => g.title === activeSub);

  // Útvonalváltáskor a menülap becsukódik
  useEffect(() => {
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Nyitáskor a főnézetről induljon, és a háttér ne görögjön
  useEffect(() => {
    if (open) {
      setActiveSub(null);
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [open]);

  // Escape zárja a menülapot
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const handleLogout = async () => {
    onClose();
    await logout();
    navigate('/login');
  };

  const isCurrentPage = (href: string) => location.pathname === href;

  return (
    <div className={`lg:hidden ${open ? '' : 'pointer-events-none'}`} aria-hidden={!open}>
      {/* Scrim */}
      <div
        className={`fixed inset-0 z-[60] bg-gray-900/50 transition-[opacity,visibility] duration-300 ${
          open ? 'opacity-100' : 'invisible opacity-0'
        }`}
        onClick={onClose}
      />

      {/* Sheet — zárt állapotban visibility:hidden, hogy a tartalma ne legyen fókuszálható */}
      <div
        className={`fixed inset-0 z-[70] flex flex-col bg-white transition-[transform,visibility] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
          open ? 'translate-y-0' : 'invisible translate-y-full'
        }`}
        // Zárva nem dialógus: a képernyőolvasó ne jelentsen be egy nem látható ablakot
        role={open ? 'dialog' : undefined}
        aria-modal={open ? true : undefined}
        aria-label="Menü"
      >
        {/* Fejléc */}
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 px-4">
          <div className="flex items-center gap-2">
            {activeSub ? (
              <button
                type="button"
                onClick={() => setActiveSub(null)}
                className="flex h-9 w-9 -ml-2 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
                aria-label="Vissza"
              >
                <ChevronLeftIcon className="h-6 w-6" />
              </button>
            ) : (
              <img
                src={clubLogoSrc(clubSettings?.crestUrl)}
                alt=""
                className="h-8 w-8 object-contain"
                onError={handleClubLogoError}
              />
            )}
            <h2 className="text-base font-semibold text-gray-900">
              {activeSub ?? displayName(clubSettings?.clubName)}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            aria-label="Bezárás"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Csúszó panelek */}
        <div className="relative flex-1 overflow-hidden">
          <div
            className={`flex h-full w-[200%] transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] ${
              activeSub ? '-translate-x-1/2' : ''
            }`}
          >
            {/* Főnézet: csoportkártyák + fiók */}
            <div className="h-full w-1/2 overflow-y-auto px-4 pt-4 pb-safe-4">
              <div className="grid grid-cols-2 gap-3">
                {groups.map((group) => (
                  <button
                    key={group.title}
                    type="button"
                    onClick={() => setActiveSub(group.title)}
                    className="flex flex-col items-start gap-2 rounded-xl border border-gray-200 bg-white p-4 text-left shadow-sm hover:bg-gray-50 transition-colors"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                      <group.icon className="h-6 w-6" />
                    </span>
                    <span className="flex w-full items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">{group.title}</span>
                      <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                    </span>
                    <span className="text-xs text-gray-500">{group.items.length} menüpont</span>
                  </button>
                ))}
              </div>

              {/* Fiók */}
              <div className="mt-6 rounded-xl border border-gray-200 bg-white shadow-sm divide-y divide-gray-100">
                <div className="flex items-center gap-3 px-4 py-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <UserCircleIcon className="h-7 w-7" />
                  </span>
                  <span>
                    <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                    <p className="text-xs text-gray-500">{describeRoles(user)}</p>
                  </span>
                </div>
                <Link
                  to="/profile"
                  onClick={onClose}
                  className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Cog6ToothIcon className="h-5 w-5 text-gray-400" />
                  Profil beállítások
                </Link>
                <Link
                  to="/features"
                  onClick={onClose}
                  className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <SparklesIcon className="h-5 w-5 text-gray-400" />
                  <span className="flex-1">Funkciók és újdonságok</span>
                  {hasNews && <span className="h-2.5 w-2.5 rounded-full bg-amber-500" aria-label="Van új funkció" />}
                </Link>
                <Link
                  to="/help"
                  onClick={onClose}
                  className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <QuestionMarkCircleIcon className="h-5 w-5 text-gray-400" />
                  Súgó
                </Link>
                <div className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700">
                  <AcademicCapIcon className="h-5 w-5 text-gray-400" />
                  <span className="flex-1">Tanuló mód</span>
                  <LearningModeSwitch />
                </div>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="flex w-full items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-gray-50 transition-colors"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5 text-red-400" />
                  Kijelentkezés
                </button>
              </div>
            </div>

            {/* Almenü nézet */}
            <div className="h-full w-1/2 overflow-y-auto px-4 pt-4 pb-safe-4">
              <div className="space-y-1">
                {activeGroup?.items.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={onClose}
                    className={`flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors ${
                      isCurrentPage(item.href)
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileMenuSheet;
